import React, { useState } from 'react';
import { Button, EmptyState, ListItem, PageHeader, TextInput } from '@origo/origo-ui';
import { DatabaseIcon, CaretRightIcon, MagnifyingGlassIcon } from '@phosphor-icons/react';
import { useDatasets } from '../../api/hooks';
import { PanelError, PanelLoading } from '../common/PanelStates';
import { DatasetDetail } from './DatasetDetail';
import type { DatasetSummary } from '../../api/types';
import styles from '../common/panel.module.css';

interface DatasetsPanelProps {
    defaultSystem: string;
}

export const DatasetsPanel: React.FC<DatasetsPanelProps> = ({ defaultSystem }) => {
    // System brukeren har bekreftet (driver spørringen) vs. tekstfeltet.
    const [system, setSystem] = useState(defaultSystem);
    const [systemInput, setSystemInput] = useState(defaultSystem);
    const [selected, setSelected] = useState<DatasetSummary | null>(null);

    const { data: datasets, isLoading, isError, error, refetch } = useDatasets(system);

    if (selected) {
        return <DatasetDetail system={system} dataset={selected} onBack={() => setSelected(null)} />;
    }

    return (
        <>
            <PageHeader
                title="Datasett"
                description="Søk opp datasett i et forvaltningssystem og inspiser skjemaet."
            />

            <div className={styles.scroll}>
                <form
                    onSubmit={(e) => {
                        e.preventDefault();
                        setSystem(systemInput.trim());
                    }}
                    className={styles.searchForm}
                >
                    <div className={styles.searchField}>
                        <TextInput
                            label="Forvaltningssystem"
                            value={systemInput}
                            onChange={(e) => setSystemInput(e.target.value)}
                            placeholder="ngis"
                            helperText="Systemnøkkel i forvaltning-api."
                        />
                    </div>
                    <Button variant="secondary" type="submit" leftIcon={<MagnifyingGlassIcon size={16} weight="bold" />}>
                        Hent
                    </Button>
                </form>

                {isLoading && <PanelLoading label={`Laster datasett for «${system}»…`} />}
                {isError && <PanelError error={error} onRetry={() => refetch()} />}

                {!isLoading && !isError && datasets && datasets.length === 0 && (
                    <EmptyState
                        icon={<DatabaseIcon size={32} weight="duotone" />}
                        title="Ingen datasett funnet"
                        description={`Forvaltningssystemet «${system}» returnerte ingen datasett.`}
                    />
                )}

                {!isLoading && !isError && datasets && datasets.length > 0 && (
                    <div className={styles.list}>
                        {datasets.map((d) => (
                            <ListItem
                                key={d.datasetId}
                                onClick={() => setSelected(d)}
                                leading={<DatabaseIcon size={18} weight="duotone" />}
                                trailing={<CaretRightIcon size={16} />}
                            >
                                <div>
                                    <div>{d.name}</div>
                                    <div className={styles.itemSubtitle}>{d.geometryType ?? 'ukjent geometri'}</div>
                                </div>
                            </ListItem>
                        ))}
                    </div>
                )}
            </div>
        </>
    );
};
